import { inject } from "@angular/core";
import { Router } from "@angular/router";

import { catchError, map, of, switchMap, tap, withLatestFrom } from "rxjs";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";

import * as authActions from './auth.actions';
import * as operatorActions from '../operators/operators.actions';
import * as authSelector from './auth.selectors';
import * as uiActions from '../ui/ui.actions';

import { AuthService } from "../../features/login/services/auth.service";
import { ErrorUtil } from "../../shared/utils/error.util";
import { MessageTranslateService } from "../../shared/services/message.service";

export class AuthEffects {
  actions$: Actions = inject(Actions);
  authService: AuthService = inject(AuthService);
  messageService = inject(MessageTranslateService)
  store: Store = inject(Store);
  router: Router = inject(Router);

  checkLogin$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.checkLogin),
        switchMap(
          () => {
            try {
              const userStr = localStorage.getItem('user');
              const accessToken = localStorage.getItem('accessToken');
              const refreshToken = localStorage.getItem('refreshToken');
              if (userStr && accessToken && refreshToken) {
                const user = JSON.parse(userStr);
                return of(authActions.checkLoginSuccess({ user, accessToken, refreshToken }))
              } else {
                return of(authActions.checkLoginFailure({ error: { errorCode: 500, errorMessage: 'User or token not found' } }))
              }
            } catch (e: unknown) {
              return of(authActions.checkLoginFailure({ error: ErrorUtil.convertToErrorModel(e) }));
            }
          }
        )
      )
  )

  preloadReqData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.checkLoginSuccess, authActions.submitLoginRequestSuccess),
        withLatestFrom(this.store.select(authSelector.selectUser)),
        map(([_res, user]) =>   operatorActions.LoadOperatorsOnLogin({defaultOperatorId : user?.company?.cmsOperatorReference}) ) 
      )
  )

  submitLoginRequest$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.submitLoginRequest),
        switchMap(
          ({ email, password, rememberMe }) =>
            this.authService.login(email, password).pipe(
              map(res => authActions.submitLoginRequestSuccess({ user: res.user, accessToken: res.accessToken, refreshToken: res.refreshToken, rememberMe: rememberMe })),
              catchError(err => of(authActions.submitLoginRequestFailure({ error: { errorCode: err.status,  errorMessage: err.error || 'Login unsuccessful' }})))
            )
        )
      )
  )

  loadToLocalStorage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.submitLoginRequestSuccess),
        tap(({ user, accessToken, refreshToken, rememberMe }) => {
          if (rememberMe) {
            localStorage.setItem('user', JSON.stringify(user));
            localStorage.setItem('accessToken', accessToken);
            localStorage.setItem('refreshToken', refreshToken);
          }
        })
      ), { dispatch: false }
  )

  loginSuccessful$ = createEffect(
    () => 
      this.actions$.pipe(
        ofType(authActions.loginSuccessful),
        map(_res => uiActions.showHeader())
      )
  )

  submitLogoutRequest$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.submitLogoutRequest),
        switchMap(
          ({ email, refreshToken }) =>
            this.authService.logout(email, refreshToken!).pipe(
              map(() => authActions.submitLogoutRequestSuccess()),
              catchError(err => of(authActions.submitLogoutRequestFailure(err)))
            )
        )
      )
  )

  submitPasswordResetRequest$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.submitPasswordResetRequest),
        switchMap(
          ({ password, resetKey }) =>
            this.authService.resetPassword(password, resetKey!).pipe(
              map(() => authActions.submitPasswordResetRequestSuccess()),
              catchError(err => of(authActions.submitPasswordResetRequestFailure(err)))
            )
        )
      )
  )

  passwordForgetOrResetSuccessful$ = createEffect(
    () => 
      this.actions$.pipe(
        ofType(
          authActions.submitPasswordResetRequestSuccess, 
          authActions.submitForgotPasswordRequestSuccess),
        tap(() => this.router.navigate(['/login']))
      ),
    { dispatch: false }
  )

  submitForgotPasswordRequest$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.submitForgotPasswordRequest),
        switchMap(
          ({ email }) =>
            this.authService.forgotPassword(email!).pipe(
              map(() => authActions.submitForgotPasswordRequestSuccess()),
              catchError(err => of(authActions.submitForgotPasswordRequestFailure(err)))
            )
        )
      )
  )

  removeFromLocalStorage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.submitLogoutRequestSuccess, authActions.loginUnsuccessful),
        tap(() => {
          localStorage.removeItem('user');
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
        })
      ), { dispatch: false }
  )

  showSuccessNotification$ = createEffect(() =>
    this.actions$.pipe(
        ofType(
            authActions.submitPasswordResetRequestSuccess,
            authActions.submitForgotPasswordRequestSuccess
        ),
        map((action) => {
            if (action.type == authActions.submitPasswordResetRequestSuccess.type) {
                this.messageService.successTranslate('login.password-reset-success');
            } else if (action.type == authActions.submitForgotPasswordRequestSuccess.type) {
                this.messageService.successTranslate('login.forgot-password-success');
            }
        })
    ),
    { dispatch: false }
  )

  showErrorNotification$ = createEffect(() =>
    this.actions$.pipe(
        ofType(
            authActions.submitLoginRequestFailure,
            authActions.submitLogoutRequestFailure
        ),
        map((action) => {
            if (action.type == authActions.submitLoginRequestFailure.type) {
                this.messageService.errorTranslate('login.login-error');
            } else if (action.type == authActions.submitLogoutRequestFailure.type) {
                this.messageService.errorMessage(action.error.errorMessage!);
            }
        })
    ),
    { dispatch: false }
  )
}
